import React, { createContext, useContext, useState, useEffect } from 'react';
import { isTokenExpired } from '../services/apiService';

interface AuthContextType {
  isAuthenticated: boolean;
  userEmail: string | null;
  userRole: 'Admin' | 'Student' | 'Staff' | null;
  loading: boolean;
  authMethod: 'cognito' | 'entra' | null;
  entraUser: any | null;
  login: (email: string, role: 'Admin' | 'Student', tokens: { idToken: string; accessToken: string }) => void;
  loginWithEntra: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<'Admin' | 'Student' | 'Staff' | null>(null);
  const [loading, setLoading] = useState(true);
  const [authMethod, setAuthMethod] = useState<'cognito' | 'entra' | null>(null);
  const [entraUser, setEntraUser] = useState<any | null>(null);

  useEffect(() => {
    // Returns true if a valid, unexpired session is present.
    const sessionIsValid = (): boolean => {
      const token = sessionStorage.getItem('idToken');
      const email = sessionStorage.getItem('userEmail');
      const role = sessionStorage.getItem('userRole') as 'Admin' | 'Student' | null;
      if (!token || !email || !role) return false;
      if (isTokenExpired(token)) return false;
      setUserEmail(email);
      setUserRole(role);
      setAuthMethod('cognito');
      return true;
    };

    const checkAuth = () => {
      try {
        if (sessionIsValid()) {
          setIsAuthenticated(true);
        } else {
          sessionStorage.clear();
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        sessionStorage.clear();
        setIsAuthenticated(false);
      }
      setLoading(false);
    };

    checkAuth();

    // Re-check every 60s so a tab left open past token expiry (4h) drops to the
    // login screen on its own, rather than presenting a working-looking UI whose
    // every action silently fails.
    const interval = setInterval(() => {
      const token = sessionStorage.getItem('idToken');
      if (token && isTokenExpired(token)) {
        sessionStorage.clear();
        setIsAuthenticated(false);
        setUserEmail(null);
        setUserRole(null);
        setAuthMethod(null);
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
      }
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const loginWithEntra = async () => {
    throw new Error('Entra ID authentication not available. Please install @azure/msal-browser and @azure/msal-react packages and run setup-azure.sh');
  };

  const login = (email: string, role: 'Admin' | 'Student', tokens: { idToken: string; accessToken: string }) => {
    sessionStorage.setItem('idToken', tokens.idToken);
    sessionStorage.setItem('accessToken', tokens.accessToken);
    sessionStorage.setItem('userEmail', email);
    sessionStorage.setItem('userRole', role);
    
    setIsAuthenticated(true);
    setUserEmail(email);
    setUserRole(role);
    setAuthMethod('cognito');
    setEntraUser(null);
  };

  const logout = async () => {
    try {
      sessionStorage.clear();
      
      if (typeof window !== 'undefined') {
        const forms = document.querySelectorAll('form');
        forms.forEach(form => {
          if (form instanceof HTMLFormElement) {
            form.reset();
          }
        });
      }
      
      setIsAuthenticated(false);
      setUserEmail(null);
      setUserRole(null);
      setAuthMethod(null);
      setEntraUser(null);
      
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
      window.location.reload();
    }
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      userEmail,
      userRole,
      loading,
      authMethod,
      entraUser,
      login,
      loginWithEntra,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
